/**
 * Shared formatters + tiny SVG charts. Pulled out of Analytics.tsx so
 * the per-device pages (DeviceDetail's Demo experience + Telemetry
 * history) can reuse them without dragging the whole Analytics page in.
 */
import { useEffect, useState } from 'react';

// ─── ticking "now" ─────────────────────────────────────────────────────
//
// React Query does structural sharing — when an offline device's last_seen
// stops moving, every refetch returns deep-equal data and React skips the
// re-render. That freezes any fmtAgo() output: "50 s ago" sits there for
// minutes until something else triggers a re-render. useNow() decouples
// time-relative display refresh from query refresh by ticking on its own
// interval. Use one call near the root of a page; every fmtAgo() in that
// subtree re-runs naturally because the component re-renders on each tick.
//
// intervalMs defaults to 1000 (1s) for "X s ago" precision. Bump to
// 30_000 on lists with many rows where second-by-second precision is
// noise (e.g. Dashboard column "5 min ago" doesn't need to tick to
// "5 min 1 s ago").

export function useNow(intervalMs: number = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ─── relative-time formatter ───────────────────────────────────────────
//
// Accepts ISO strings, epoch-seconds numbers, or epoch-ms numbers. Picks
// s / min / h / d so the operator reads natural units without arithmetic.

export function fmtAgo(
  ts: string | number | Date | null | undefined,
  // Injectable clock. Callers pass it when the relative age has to agree with
  // other numbers derived from the same instant (or in tests); everything else
  // keeps the old one-argument call.
  nowMs: number = Date.now(),
): string {
  if (ts === null || ts === undefined) return '—';
  let ms: number;
  if (ts instanceof Date) {
    ms = ts.getTime();
  } else if (typeof ts === 'number') {
    // < 10^12 is almost certainly epoch-seconds (≈ 2001); ≥ 10^12 is epoch-ms.
    ms = ts < 1e12 ? ts * 1000 : ts;
  } else {
    ms = Date.parse(ts);
  }
  if (!Number.isFinite(ms)) return '—';
  const delta = Math.max(0, (nowMs - ms) / 1000);
  if (delta < 60) return `${Math.round(delta)} s ago`;
  if (delta < 3600) return `${Math.round(delta / 60)} min ago`;
  if (delta < 86400) return `${Math.round(delta / 3600)} h ago`;
  return `${Math.round(delta / 86400)} d ago`;
}

// ─── day-separator label ───────────────────────────────────────────────
//
// Label for the date rows that break up a chronological list: "Today" /
// "Yesterday" for the two most recent local days, else an absolute date in
// the app's established style ("Mon, 25 May 2026"). Local-day math (not UTC)
// so it agrees with the toLocaleTimeString() shown alongside each row. nowMs
// is injectable for tests.

export function fmtDateHeader(
  ts: string | number | Date | null | undefined,
  nowMs: number = Date.now(),
): string {
  if (ts === null || ts === undefined) return '—';
  const d =
    ts instanceof Date
      ? ts
      : new Date(typeof ts === 'number' ? (ts < 1e12 ? ts * 1000 : ts) : ts);
  if (Number.isNaN(d.getTime())) return '—';
  const startOfLocalDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round(
    (startOfLocalDay(new Date(nowMs)) - startOfLocalDay(d)) / 86_400_000,
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── number formatters ─────────────────────────────────────────────────

export function fmtMs(ms: number | string): string {
  const n = typeof ms === 'string' ? Number(ms) : ms;
  if (!Number.isFinite(n) || n === 0) return '—';
  if (n < 1_000) return `${Math.round(n)} ms`;
  if (n < 60_000) return `${(n / 1_000).toFixed(1)} s`;
  return `${(n / 60_000).toFixed(1)} min`;
}

export function fmtS(s: number | string): string {
  const n = typeof s === 'string' ? Number(s) : s;
  if (!Number.isFinite(n) || n === 0) return '—';
  if (n < 60) return `${n.toFixed(1)} s`;
  if (n < 3600) return `${(n / 60).toFixed(1)} min`;
  return `${(n / 3600).toFixed(1)} h`;
}

export function fmtPct(rate: string | null): string {
  if (rate === null) return '—';
  const n = Number(rate);
  return `${Math.round(n * 100)}%`;
}

// ─── duration formatter (sessions) ─────────────────────────────────────
//
// fmtS picks one unit and floats — good for averages. fmtDuration emits
// a tight composite that drops noise at the larger scales: "1h 7m" at
// hours-scale (seconds → rounding into minutes), "6m 42s" at minutes-
// scale, "42s" at seconds-scale. Accepts seconds (float ok); negative
// / NaN renders as "—".
export function fmtDuration(s: number | string): string {
  const n = typeof s === 'string' ? Number(s) : s;
  if (!Number.isFinite(n) || n < 0) return '—';
  const total = Math.round(n);
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) {
    // Round leftover seconds into minutes so we don't render a third
    // unit at the hours scale. 30 s is the conventional banker's
    // boundary; 59 s rolls minutes up by one.
    let mr = sec >= 30 ? m + 1 : m;
    let hr = h;
    if (mr === 60) {
      mr = 0;
      hr += 1;
    }
    return mr > 0 ? `${hr}h ${mr}m` : `${hr}h`;
  }
  return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
}

// ─── content formatters (DE-171) ───────────────────────────────────────
// fmtBytes: human file size. Accepts a number or a pg-bigint string.
export function fmtBytes(b: string | number | null | undefined): string {
  const n = typeof b === 'string' ? Number(b) : b;
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// fmtMSS: media clock "m:ss" (e.g. 0:42, 3:07). Distinct from fmtDuration's
// composite "1h 7m"; used for short clip durations. Null/NaN → ''.
export function fmtMSS(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// shortSha: first 7 hex chars for a compact display (full value in a title=).
export function shortSha(s: string | null | undefined): string {
  return s ? s.slice(0, 7) : '';
}

// Glyph per content file kind — shared by the library item cards + the device
// content drill-down. Fall back to '≡' for unknown/null kinds at the call site.
export const KIND_GLYPH: Record<string, string> = {
  video: '▶', audio: '♪', image: '▣', sidecar: '≡', subtitle: '≣',
};

// ─── ULID timestamp decoder ────────────────────────────────────────────
//
// ULIDs (Crockford-base32) encode 48 bits of Unix-ms in the first 10
// characters. We use them as session_ids (minted by usage-aggregator.py
// on demo_mode transition into Active); recovering the start time lets
// the SPA show "session started 14:23" without an extra round-trip.
//
// Mirror of iot-gateway.py _generate_ulid()'s encode loop.
const _ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const _ULID_INDEX: Record<string, number> = Object.fromEntries(
  _ULID_ALPHABET.split('').map((c, i) => [c, i]),
);

export function ulidTimestampMs(id: string): number | null {
  if (typeof id !== 'string' || id.length < 10) return null;
  const head = id.slice(0, 10).toUpperCase();
  let ts = 0;
  for (const c of head) {
    const v = _ULID_INDEX[c];
    if (v === undefined) return null;
    ts = ts * 32 + v;
    if (!Number.isSafeInteger(ts)) return null;
  }
  return ts;
}

// ─── haversine distance (metres) ───────────────────────────────────────
//
// Used in the location-history page to annotate "moved Nm from previous
// fix" between consecutive points. Mirror of the Lambda's _haversine_m.
export function haversineMeters(
  lat1: number, lon1: number, lat2: number, lon2: number,
): number {
  const r = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const a =
    Math.sin(dp / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}

// ─── address vs venue name de-duplication (DE-170) ────────────────────
//
// Geoapify's `formatted` address usually LEADS with the POI name, so any
// surface that shows the venue name above the address repeats it:
//   Compton High School
//   Compton High School, 601 South Acacia Ave, Compton, CA …
// Strip the leading copy so the second line carries only new information.
//
// Deliberately conservative:
//   - Case-insensitive match, but the ORIGINAL address text is returned, so
//     the provider's capitalisation survives.
//   - Only strips when a separator (comma/space/dash) follows, so a venue
//     called "Park" can't behead "Parkgatan 5".
//   - Returns null when nothing but the venue name remains, letting callers
//     skip the line entirely instead of rendering an empty muted row.
// Sometimes the remainder is genuinely useful ("Las Vegas Convention Center"
// + "… North Hall, 3150 Paradise Road" keeps the hall), which is why this
// strips a prefix rather than dropping the address wholesale.
/**
 * Turn a Geoapify place category into something an operator can read:
 * `conference_centre` → "Conference centre".
 *
 * Used when a fix has a `place_type` but no `place_name` — the device is
 * inside a feature that carries no name in the underlying open data (ExCeL
 * London's Phase 2 hall is mapped as a conference centre with no `name` tag).
 * Naming the kind of place is honest and useful; borrowing a neighbouring
 * building's proper noun, which is what the resolver used to do, is neither.
 */
export function humanisePlaceType(
  placeType: string | null | undefined,
): string | null {
  const t = (placeType ?? '').trim();
  if (!t) return null;
  const words = t.replace(/[_.-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!words) return null;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function addressWithoutVenue(
  address: string | null | undefined,
  placeName: string | null | undefined,
): string | null {
  if (!address) return null;
  const addr = address.trim();
  const venue = (placeName ?? '').trim();
  if (!venue || !addr.toLowerCase().startsWith(venue.toLowerCase())) {
    return addr || null;
  }
  const rest = addr.slice(venue.length);
  // Require a separator; otherwise this is a different word that merely
  // shares the venue's opening characters.
  if (rest && !/^[\s,;:—–-]/.test(rest)) return addr;
  return rest.replace(/^[\s,;:—–-]+/, '').trim() || null;
}

// ─── BarChart: SVG, percentage-coordinate viewBox ─────────────────────
//
// Optional `tooltip` and `onBarClick` make individual bars hoverable +
// clickable. When neither is provided the chart renders static, same as
// the original Phase-1 use site. The generic `Bar` type lets callers
// thread per-bar metadata through to the callbacks without losing types.

export type BarDatum<M = undefined> = {
  label: string;
  value: number;
  meta?: M;
};

export function BarChart<M = undefined>({
  data,
  format = (v) => String(v),
  height = 140,
  tooltip,
  onBarClick,
}: {
  data: Array<BarDatum<M>>;
  format?: (v: number) => string;
  height?: number;
  tooltip?: (item: BarDatum<M>) => React.ReactNode;
  onBarClick?: (item: BarDatum<M>) => void;
}) {
  const [hover, setHover] = useState<{ index: number; x: number; y: number } | null>(null);

  if (data.length === 0) {
    return <p className="text-sm text-muted">No data in this window.</p>;
  }
  const max = Math.max(...data.map((d) => d.value), 1);
  const barW = 100 / data.length;
  const interactive = Boolean(tooltip || onBarClick);
  const hoveredDatum = hover ? data[hover.index] : null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 100 ${height + 28}`} preserveAspectRatio="none" className="w-full">
        {data.map((d, i) => {
          const h = (d.value / max) * height;
          return (
            <g key={`${d.label}-${i}`}>
              <rect
                x={i * barW + barW * 0.1}
                y={height - h}
                width={barW * 0.8}
                height={h}
                rx={0.6}
                className={
                  'fill-accent/80' +
                  (interactive ? ' cursor-pointer hover:fill-accent' : '')
                }
                onMouseEnter={interactive ? (e) => {
                  const r = (e.currentTarget.ownerSVGElement as SVGSVGElement)
                    .getBoundingClientRect();
                  setHover({
                    index: i,
                    x: e.clientX - r.left,
                    y: e.clientY - r.top,
                  });
                } : undefined}
                onMouseMove={interactive ? (e) => {
                  const r = (e.currentTarget.ownerSVGElement as SVGSVGElement)
                    .getBoundingClientRect();
                  setHover({
                    index: i,
                    x: e.clientX - r.left,
                    y: e.clientY - r.top,
                  });
                } : undefined}
                onMouseLeave={interactive ? () => setHover(null) : undefined}
                onClick={onBarClick ? () => onBarClick(d) : undefined}
              />
              <text
                x={i * barW + barW * 0.5}
                y={height + 8}
                fontSize="3"
                textAnchor="middle"
                className="fill-fg pointer-events-none"
              >
                {format(d.value)}
              </text>
              <text
                x={i * barW + barW * 0.5}
                y={height + 16}
                fontSize="2.5"
                textAnchor="middle"
                className="fill-muted pointer-events-none"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
      {tooltip && hover && hoveredDatum && (
        <div
          // Absolutely positioned next to the cursor; pointer-events-none
          // so the tooltip itself doesn't steal hover from the bar it's
          // anchored to. Clamped to right edge to avoid overflow on the
          // rightmost bars.
          className="absolute z-10 px-2 py-1 rounded-md bg-fg text-bg text-xs shadow pointer-events-none whitespace-nowrap"
          style={{
            left: Math.min(hover.x + 12, 100000),
            top: hover.y + 12,
            transform: hover.x > 200 ? 'translateX(-100%)' : undefined,
          }}
        >
          {tooltip(hoveredDatum)}
        </div>
      )}
    </div>
  );
}

// LineChart was removed when DeviceTelemetryChart was replaced by the
// synchronized TelemetryChartGroup (DE-153). If you need a small
// generic line plot somewhere else, see portal/src/components/telemetry/
// for the registry-driven approach.
